import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { guidFor } from "@ember/object/internals";
import { getOwner, setOwner } from "@ember/owner";
import { cancel } from "@ember/runloop";
import { service } from "@ember/service";
import { MENU } from "discourse/float-kit/lib/constants";
import FloatKitInstance from "discourse/float-kit/lib/float-kit-instance";
import { animateClosing } from "discourse/lib/animation-utils";

export default class DMenuInstance extends FloatKitInstance {
  @service menu;
  @service site;
  @service modal;

  /**
   * Indicates whether the menu is expanded or not.
   * @property {boolean} expanded - Tracks the state of menu expansion, initially set to false.
   */
  @tracked expanded = false;

  /**
   * Specifies whether the trigger for opening/closing the menu is detached from the menu itself.
   * This is the case when a menu is trigger programmatically instead of through the <DMenu /> component.
   * @property {boolean} detachedTrigger - Tracks whether the trigger is detached, initially set to false.
   */
  @tracked detachedTrigger = false;

  /**
   * Configuration options for the DMenuInstance.
   * @property {Object} options - Options object that configures the menu behavior and display.
   */
  @tracked options;
  @tracked portalOutletOverrideElement;

  @tracked _trigger;

  constructor(owner, options = {}) {
    super(...arguments);

    setOwner(this, owner);
    this.options = { ...MENU.options, ...options };
    this.portalOutletOverrideElement = options.portalOutletElement;
  }

  get portalOutletElement() {
    return (
      this.portalOutletOverrideElement ||
      document.getElementById("d-menu-portals")
    );
  }

  get trigger() {
    return this._trigger;
  }

  set trigger(element) {
    this._trigger = element;
    this.id = element.id || guidFor(element);
    this.setupListeners();
  }

  get shouldTrapPointerDown() {
    return this.expanded;
  }

  @action
  async close(options = { focusTrigger: true }) {
    this.openedByDelayedHover = false;

    if (getOwner(this).isDestroying) {
      return;
    }

    await animateClosing(this.content);

    await super.close(...arguments);

    if (this.site.mobileView && this.options.modalForMobile && this.expanded) {
      await this.modal.close();
    }

    await this.menu.close(this);

    if (options.focusTrigger) {
      this.trigger?.focus?.();
    }

    await this.options.onClose?.(this);
  }

  @action
  async show() {
    await super.show(...arguments);
    await this.menu.show(this);
  }

  @action
  async onPointerMove(event) {
    if (this.expanded && this.trigger.contains(event.target)) {
      return;
    }

    await this.onTrigger(event);
  }

  @action
  async onClick(event) {
    cancel(this.delayedHoverTimeout);

    if (this.openedByDelayedHover) {
      this.openedByDelayedHover = false;
      return;
    }

    if (this.expanded && this.untriggers.includes("click")) {
      return await this.onUntrigger(event);
    }

    await this.onTrigger(event);
  }

  @action
  async onPointerLeave(event) {
    if (this.untriggers.includes("hover")) {
      await this.onUntrigger(event);
    }
  }

  @action
  async onTrigger(event) {
    event.stopPropagation();

    await this.options.beforeTrigger?.(this);
    await this.show();
  }

  @action
  async onUntrigger(event) {
    event.stopPropagation();

    await this.close();
  }

  @action
  destroy() {
    this.close();
    this.tearDownListeners();
  }
}
